import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getChannels } from '../services/api';

const ChannelContext = createContext(null);

export const useChannel = () => {
  const context = useContext(ChannelContext);
  if (!context) {
    throw new Error('useChannel must be used within ChannelProvider');
  }
  return context;
};

const SELECTED_CHANNEL_KEY = 'selectedChannelId';

// Pages showing one menu or campaign belong to its channel: switching channel leaves them for the
// new channel's list instead of keeping the old channel's record on screen.
const channelHome = (pathname) => {
  if (pathname === '/create' || pathname.startsWith('/edit/')) return '/menus';
  if (pathname.startsWith('/campaigns/')) return '/campaigns';
  return null;
};

export const ChannelProvider = ({ children, workspaceId }) => {
  const [channels, setChannels] = useState([]);
  const [selectedChannelId, setSelectedChannelId] = useState(() => {
    return localStorage.getItem(SELECTED_CHANNEL_KEY) || null;
  });
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  const fetchChannels = useCallback(async () => {
    if (!localStorage.getItem('line_rich_menu_token')) {
      setLoading(false);
      return;
    }
    try {
      const data = await getChannels();
      setChannels(data.channels || []);

      // If selected channel no longer exists in the new workspace, clear it
      if (selectedChannelId && !data.channels?.find(c => c.id === selectedChannelId)) {
        setSelectedChannelId(null);
        localStorage.removeItem(SELECTED_CHANNEL_KEY);
      }

      // Auto-select first channel if none selected
      if (!selectedChannelId && data.channels?.length > 0) {
        const firstChannelId = data.channels[0].id;
        setSelectedChannelId(firstChannelId);
        localStorage.setItem(SELECTED_CHANNEL_KEY, firstChannelId);
      }
    } catch (error) {
      console.error('Failed to fetch channels:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedChannelId]);

  // Re-fetch channels whenever the workspace changes
  useEffect(() => {
    setLoading(true);
    setChannels([]);
    fetchChannels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const selectChannel = useCallback((channelId) => {
    setSelectedChannelId(channelId);
    if (channelId) {
      localStorage.setItem(SELECTED_CHANNEL_KEY, channelId);
    } else {
      localStorage.removeItem(SELECTED_CHANNEL_KEY);
    }
  }, []);

  // A switch requested from a menu or campaign page travels with the navigation, so it only
  // applies if the page lets the navigation through (the menu editor may ask to save first).
  const switchToChannel = location.state?.switchToChannel;
  useEffect(() => {
    if (!switchToChannel) return;
    selectChannel(switchToChannel);
    navigate(location.pathname + location.search, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [switchToChannel]);

  const switchChannel = useCallback((channelId) => {
    if (channelId === selectedChannelId) return;
    const home = channelHome(location.pathname);
    if (home) {
      navigate(home, { state: { switchToChannel: channelId } });
    } else {
      selectChannel(channelId);
    }
  }, [selectedChannelId, location.pathname, navigate, selectChannel]);

  const selectedChannel = channels.find(c => c.id === selectedChannelId) || null;

  const value = {
    channels,
    selectedChannelId,
    selectedChannel,
    selectChannel,
    switchChannel,
    refreshChannels: fetchChannels,
    loading
  };

  return (
    <ChannelContext.Provider value={value}>
      {children}
    </ChannelContext.Provider>
  );
};

export default ChannelContext;
